/**
 * Página para crear/editar roles
 * Sistema Klever Octopus - Betel Natural
 */
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { usePermissions } from '../../hooks';
import { rolesService } from '../../services';
import RoleForm from './RoleForm';
import { Loading } from '../../components/UI/Loading';

const RoleFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const { permissions: apiPermissions, loading: permissionsLoading, error: permissionsError, loadPermissions } = usePermissions();
  const [role, setRole] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // NO crear permisos sintéticos - usar solo los que vienen del backend
  // Si no hay permisos, mostrar mensaje para ejecutar el seed
  const permissions = React.useMemo(() => {
    return (apiPermissions || []).filter(permission => permission.is_active !== false && permission.name !== 'ventas.stage.released');
  }, [apiPermissions]);

  // Cargar rol si es modo edición
  React.useEffect(() => {
    const loadRole = async () => {
      if (!isEditMode) return;

      setLoading(true);
      setError(null);

      try {
        const response = await rolesService.getRole(id);

        if (response.success) {
          setRole(response.data);
        } else {
          setError(response.message || 'Error al cargar el rol');
        }
      } catch (err) {
        console.error('Error loading role:', err);
        setError('Error al cargar el rol');
      } finally {
        setLoading(false);
      }
    };

    loadRole();
  }, [id, isEditMode]);

  const handleSubmit = async (roleData) => {
    try {
      // Filtrar permisos sintéticos (solo enviar IDs numéricos válidos)
      const filteredPermissions = roleData.permissions.filter(permId => {
        // Solo incluir permisos con ID numérico (no strings tipo "synthetic_0")
        return typeof permId === 'number' || (typeof permId === 'string' && !permId.startsWith('synthetic_'));
      });

      // Convertir a números si son strings numéricos
      const numericPermissions = filteredPermissions.map(id =>
        typeof id === 'string' ? parseInt(id, 10) : id
      );

      // Crear datos del rol con permisos filtrados
      const cleanedRoleData = {
        ...roleData,
        permissions: numericPermissions
      };

      let result;

      if (isEditMode) {
        result = await rolesService.updateRole(id, cleanedRoleData);
      } else {
        result = await rolesService.createRole(cleanedRoleData);
      }

      if (result.success) {
        // Navegar de vuelta a la lista de roles
        navigate('/system/admin/roles');
      } else {
        return result;
      }
    } catch (error) {
      console.error('Error submitting role:', error);
      return {
        success: false,
        message: 'Error al guardar el rol'
      };
    }
  };

  const handleCancel = () => {
    navigate('/system/admin/roles');
  };

  if (loading || permissionsLoading) {
    return (
      <div className="page-loading">
        <Loading message={isEditMode ? "Cargando rol..." : "Cargando permisos..."} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="role-form-page">
        <div className="page-header">
          <button
            className="btn btn--ghost"
            onClick={handleCancel}
            type="button"
          >
            <FiArrowLeft /> Volver a Roles
          </button>
        </div>
        <div className="alert alert--danger">
          {error}
        </div>
      </div>
    );
  }

  // Advertencia si no hay permisos en el sistema (solo mostrar si ya terminó de cargar y está vacío)
  const noPermissionsWarning = !permissionsLoading && permissions.length === 0 && (
    <div style={{
      background: '#fff3cd',
      border: '1px solid #ffc107',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'start',
      gap: '12px'
    }}>
      <i className="fas fa-exclamation-triangle" style={{ color: '#856404', fontSize: '20px' }}></i>
      <div>
        <strong style={{ color: '#856404' }}>
          {permissionsError ? 'No se pudieron cargar los permisos' : 'No hay permisos disponibles'}
        </strong>
        <p style={{ margin: '8px 0 0 0', color: '#856404' }}>
          {permissionsError
            ? `Error al consultar los permisos: ${permissionsError}. Intenta cargarlos de nuevo.`
            : 'El servidor devolvió una lista vacía de permisos. No guardes el rol hasta verificar el catálogo.'}
        </p>
        <button type="button" className="btn btn--outline" onClick={() => loadPermissions()}
          style={{ marginTop: '12px' }}>Reintentar carga</button>
      </div>
    </div>
  );

  return (
    <div className="role-form-page">
      {/* Header con gradiente corporativo Betel Natural */}
      <div className="page-header" style={{
        background: 'linear-gradient(135deg, #7B2D8E 0%, #834594 100%)',
        padding: '24px 40px',
        marginBottom: '30px',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(123, 45, 142, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <button
            className="btn btn--ghost"
            onClick={handleCancel}
            type="button"
            style={{
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              padding: '10px 16px'
            }}
          >
            <FiArrowLeft /> Volver
          </button>

          <div style={{ flex: 1 }}>
            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: '0',
              color: 'white'
            }}>
              {isEditMode ? 'Editar Rol' : 'Crear Nuevo Rol'}
            </h1>
            <p style={{
              margin: '8px 0 0 0',
              opacity: 0.95,
              fontSize: '14px',
              fontWeight: '400',
              color: 'white'
            }}>
              {isEditMode
                ? 'Modifica la información y permisos del rol'
                : 'Configura el nombre, nivel y permisos del nuevo rol'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Advertencia de permisos */}
      {noPermissionsWarning}

      {/* Formulario */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        maxWidth: '1600px',
        margin: '0 auto'
      }}>
        <RoleForm
          role={role}
          mode={isEditMode ? 'edit' : 'create'}
          permissions={permissions}
          onSubmit={permissions.length === 0 ? () => Promise.resolve({ success: false, message: 'Espera a que carguen los permisos antes de guardar el rol.' }) : handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
};

export default RoleFormPage;
